import { Box, Stack } from "@mui/material";

import { ResetFiltersButton } from "components/core/ResetFiltersButton.tsx";
import { EquipmentTypeMultiSelect } from "components/EquipmentTypeSelect";
import { ProvinceStateMultiSelect } from "components/ProvinceStateMultiSelect";
import { ShipmentServiceMultiSelect } from "components/ShipmentServiceMultiSelect";
import { ShipmentStatusMultiSelect } from "components/ShipmentStatusSelect";

import { QuoteDateRangeFilter } from "../QuoteDateRangeFilter";
import { QuoteShipmentTypeFilter } from "../QuoteShipmentTypeFilter";

interface Props {
  filters: QuoteFilters;
  onEquipmentTypesChange: (value: string[]) => void;
  onServiceTypesChange: (value: string[]) => void;
  onShipmentTypeChange: (value: string) => void;
  onOriginProvincesChange: (value: string[]) => void;
  onDestinationProvincesChange: (value: string[]) => void;
  onPickupDateAfterChange: (value: string) => void;
  onPickupDateBeforeChange: (value: string) => void;
  onDeliveryDateAfterChange: (value: string) => void;
  onDeliveryDateBeforeChange: (value: string) => void;
  onShipmentStatusChange: (value: string[]) => void;
  onClearFilters: VoidFunction;
  showResetButton?: boolean;
}

export function QuoteFiltersMorePanel(props: Props) {
  const {
    filters,
    onEquipmentTypesChange,
    onServiceTypesChange,
    onShipmentTypeChange,
    onOriginProvincesChange,
    onDestinationProvincesChange,
    onPickupDateAfterChange,
    onPickupDateBeforeChange,
    onDeliveryDateAfterChange,
    onDeliveryDateBeforeChange,
    onShipmentStatusChange,
    onClearFilters,
    showResetButton = true,
  } = props;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        animation: "fadeIn 0.2s ease-in-out",
        "@keyframes fadeIn": {
          from: { opacity: 0, transform: "translateY(-4px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {/* Row 1: Equipment Types, Services, Shipment Type */}
      <Stack direction="row" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <EquipmentTypeMultiSelect
            label="Equipment Types"
            onChange={onEquipmentTypesChange}
            value={filters.equipmentTypes ?? []}
            width="100%"
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <ShipmentServiceMultiSelect
            label="Services"
            value={filters.serviceTypes ?? []}
            onChange={onServiceTypesChange}
            width="100%"
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <QuoteShipmentTypeFilter
            shipmentType={filters.shipmentTypes?.[0]}
            onShipmentTypeChange={onShipmentTypeChange}
          />
        </Box>
      </Stack>

      {/* Row 2: Origins, Destinations */}
      <Stack direction="row" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <ProvinceStateMultiSelect
            label="Origins"
            value={filters.originProvinces ?? []}
            onChange={onOriginProvincesChange}
            width="100%"
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <ProvinceStateMultiSelect
            label="Destinations"
            value={filters.destinationProvinces ?? []}
            onChange={onDestinationProvincesChange}
            width="100%"
          />
        </Box>
      </Stack>

      {/* Row 3: Pickup date range */}
      <QuoteDateRangeFilter
        label="Pickup"
        from={filters.pickupDateAfter}
        to={filters.pickupDateBefore}
        onFromChange={onPickupDateAfterChange}
        onToChange={onPickupDateBeforeChange}
      />

      {/* Row 4: Delivery date range */}
      <QuoteDateRangeFilter
        label="Delivery"
        from={filters.deliveryDateAfter}
        to={filters.deliveryDateBefore}
        onFromChange={onDeliveryDateAfterChange}
        onToChange={onDeliveryDateBeforeChange}
      />

      {/* Row 5: Shipment Status + Reset */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ flex: 1 }}>
          <ShipmentStatusMultiSelect
            value={filters.shipmentStatus ?? []}
            onChange={onShipmentStatusChange}
            excludeStatuses={[]}
            label="Shipment Status"
            width="100%"
          />
        </Box>
        {showResetButton && (
          <>
            <Box sx={{ flex: 2 }} />
            <ResetFiltersButton handleResetFilters={onClearFilters} />
          </>
        )}
      </Stack>
    </Box>
  );
}
